import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { IImpactoN1 } from "../../services/materiaisN1";

/**
 * FASE 05 Ajustes 02/09 (itens A4-A6): os avisos de impacto viram um ASSISTENTE de passos nos
 * três modais de acionamento (Homologar, Concluir Homologação e Disponibilizar Mercado):
 *
 *   passo 1 — tipologias que usam os produtos alterados  → Cancelar · Copiar Lista · Continuar
 *   passo 2 — sistemistas afetadas                        → Cancelar · Copiar lista · Continuar
 *   passo 3 — a confirmação que cada modal já tinha       → Cancelar · botão da ação
 *
 * Passo sem conteúdo é pulado. Decisão P2 (TI, 02/09 17:57): o Copiar Lista do passo 1 segue as
 * colunas da tabela (Produto/Tipologia/Linha/Projetista); o do passo 2 copia "Sistemista +
 * Código Alterado + Alteração" — e NÃO o "Projetista + código alterado" que constava no docx.
 * F05B-P8 continua valendo: nenhum passo bloqueia, só informa e pede que o usuário continue.
 *
 * Um único componente para os três modais, de propósito: os textos vieram literais do reporte e
 * não podem divergir entre as telas. A cópia sai em TSV — cola direto no Excel.
 */

export type PassoImpacto = "tipologias" | "sistemistas";

/** Passos de aviso aplicáveis a este impacto, na ordem do reporte. */
export const passosDoImpacto = (impacto?: IImpactoN1 | null): PassoImpacto[] => {
  const passos: PassoImpacto[] = [];
  if (impacto?.tipologias?.length) passos.push("tipologias");
  if (impacto?.sistemistas?.length) passos.push("sistemistas");
  return passos;
};

const copiarTsv = async (cabecalho: string[], linhas: string[][]): Promise<boolean> => {
  try {
    const tsv = [cabecalho, ...linhas].map((l) => l.join("\t")).join("\n");
    await navigator.clipboard.writeText(tsv);
    return true;
  } catch {
    return false;
  }
};

interface BotoesPassoProps {
  onCancelar: () => void;
  onContinuar: () => void;
  onCopiar: () => Promise<boolean>;
}

const BotoesPasso: React.FC<BotoesPassoProps> = ({ onCancelar, onContinuar, onCopiar }) => {
  const [copiado, setCopiado] = useState(false);

  const handleCopiar = async () => {
    if (await onCopiar()) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  return (
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancelar} variant="outlined" sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
        CANCELAR
      </Button>
      <Button onClick={handleCopiar} variant="outlined" sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
        {copiado ? "COPIADO!" : "COPIAR LISTA"}
      </Button>
      <Button
        onClick={onContinuar}
        variant="contained"
        sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
      >
        CONTINUAR
      </Button>
    </DialogActions>
  );
};

interface PassoProps {
  impacto: IImpactoN1;
  onCancelar: () => void;
  onContinuar: () => void;
}

/** Passo 1 — item 3 do reporte de 31/08, texto literal. */
export const PassoImpactoTipologias: React.FC<PassoProps> = ({ impacto, onCancelar, onContinuar }) => (
  <>
    <DialogContent>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Existem produtos sendo utilizados nas tipologias disponíveis para uso no CEM
        </Typography>
      </Alert>

      <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Produto</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Tipologia</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Linha / sistema</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Projetista</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {impacto.tipologias.map((t, i) => (
              <TableRow key={`${t.codigoTipologia}-${t.codigoProduto}-${i}`}>
                <TableCell>{t.codigoProduto}</TableCell>
                <TableCell>{t.codigoTipologia}</TableCell>
                <TableCell>{t.linha}</TableCell>
                <TableCell>{t.projetista}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </DialogContent>

    <BotoesPasso
      onCancelar={onCancelar}
      onContinuar={onContinuar}
      onCopiar={() =>
        copiarTsv(
          ["Produto", "Tipologia", "Linha / sistema", "Projetista"],
          impacto.tipologias.map((t) => [t.codigoProduto, t.codigoTipologia, t.linha, t.projetista])
        )
      }
    />
  </>
);

/** Passo 2 — item 4 do reporte de 31/08, texto literal; empresas pelo NOME FANTASIA. */
export const PassoImpactoSistemistas: React.FC<PassoProps> = ({ impacto, onCancelar, onContinuar }) => {
  // Decisão P2: a lista copiada é "Sistemista + Código Alterado + Alteração".
  const pares = impacto.sistemistas.flatMap((s) =>
    (s.produtos ?? []).map((p) => [s.nomeFantasia, p.codigo, p.alteracao])
  );

  return (
    <>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Há sistemistas que utilizam os produtos com código e/ou status alterados, se for o
            caso, confirme se eles já foram contatados sobre as alterações realizadas antes de
            continuar:
          </Typography>
        </Alert>

        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Sistemista</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Código Alterado</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Alteração</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pares.length > 0 ? (
                pares.map((linha, i) => (
                  <TableRow key={i}>
                    <TableCell>{linha[0]}</TableCell>
                    <TableCell>{linha[1]}</TableCell>
                    <TableCell>{linha[2]}</TableCell>
                  </TableRow>
                ))
              ) : (
                impacto.sistemistas.map((s) => (
                  <TableRow key={s.idEmpresa}>
                    <TableCell colSpan={3}>{s.nomeFantasia}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <BotoesPasso
        onCancelar={onCancelar}
        onContinuar={onContinuar}
        onCopiar={() =>
          copiarTsv(
            ["Sistemista", "Código Alterado", "Alteração"],
            pares.length > 0 ? pares : impacto.sistemistas.map((s) => [s.nomeFantasia, "", ""])
          )
        }
      />
    </>
  );
};
